/**
 * Server-side Supabase client factories.
 *
 * Two factories live here:
 *
 *   - `createServerClient()` — RLS-aware, uses the anon key plus the
 *     user's session cookies. Pass it a cookie store from the caller's
 *     framework (e.g. Next.js `cookies()`).
 *
 *   - `createServiceRoleClient()` — bypasses RLS. Use ONLY in trusted
 *     server contexts (Stripe webhook, server actions that have already
 *     verified authorization).
 */

import { createServerClient as createSsrClient } from "@supabase/ssr";
import { createClient as createSupabaseClient } from "@supabase/supabase-js";
import type { Database } from "./types";
import { readPublicEnv, readServerEnv } from "./env";

export interface CookieToSet {
  name: string;
  value: string;
  options?: Record<string, unknown>;
}

export interface CookieAdapter {
  getAll(): { name: string; value: string }[];
  setAll(cookies: CookieToSet[]): void;
}

export function createServerClient(cookies: CookieAdapter) {
  const env = readPublicEnv();
  return createSsrClient<Database>(
    env.SUPABASE_URL,
    env.SUPABASE_ANON_KEY,
    {
      cookies: {
        getAll: () => cookies.getAll(),
        setAll: (cookiesToSet: CookieToSet[]) => {
          try {
            cookies.setAll(cookiesToSet);
          } catch {
            // Calling setAll from a Server Component is a no-op — Next.js
            // will refresh cookies on the next mutation. Swallow.
          }
        },
      },
    },
  );
}

export function createServiceRoleClient() {
  const env = readServerEnv();
  return createSupabaseClient<Database>(
    env.SUPABASE_URL,
    env.SUPABASE_SERVICE_ROLE_KEY,
    {
      auth: {
        autoRefreshToken: false,
        persistSession: false,
      },
    },
  );
}
